/*
 * The chip strip above the text box: what is going to be sent with this message, in the order it
 * will be sent (spec 07 §4 "Composer").
 *
 * Accessibility decides the shape here. WCAG 2.2 SC 2.5.7 forbids reorder that only a drag can do,
 * so a chip can be moved three ways — dragged with the pointer, moved from its own menu (the only
 * path a touch screen has), and moved with `Alt`+`←`/`→` from the keyboard — and every move is
 * announced through the application's one live region. The strip is a list with a roving tab stop:
 * one Tab reaches it whatever it holds, and the arrow keys walk it, because ten files behind two
 * controls each would put twenty stops between the text box and the send button.
 *
 * Each chip is therefore one control that opens its own menu. The close icon beside it is the same
 * Remove a pointer expects to find — the icon set rather than a glyph, so it belongs to the same
 * family as every other close in the product; it is not a second tab stop, because the keyboard
 * already has that action one keystroke away.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { cx } from "../../../lib/cx.ts"
import { Button, Dialog, Field, Menu, type MenuItem } from "../../../ui/index.ts"
import { formatBytes } from "../../i18n/format.ts"
import { t, translate } from "../../i18n/index.ts"
import { Icon, useAnnounce } from "../../shell/index.ts"
import "./attachments.css"
import type { StagedFile } from "./types.ts"
import type { TrayStore } from "./uploadManager.ts"

/** What a chip says to a screen reader: the name, the size and, while it runs, how far it has got. */
function chipLabel(file: StagedFile, index: number, total: number): string {
	const base = t("composer.attach.chip", {
		name: file.name,
		size: formatBytes(file.bytes),
		position: index + 1,
		total,
	})
	if (file.note !== undefined) return `${base} — ${file.note}${stateOf(file)}`
	return `${base}${stateOf(file)}`
}

/** How far a chip's upload has got, as the tail of its spoken name; nothing once it is there. */
function stateOf(file: StagedFile): string {
	if (file.state === "uploading") {
		return ` — ${t("composer.attach.uploading", { percent: Math.round(file.progress * 100) })}`
	}
	if (file.state === "queued") return ` — ${t("composer.attach.queued")}`
	if (file.state === "failed") return ` — ${t("composer.attach.failed")}`
	return ""
}

/** The drag's own payload: which chip is on the move. */
const CHIP_INDEX = "application/x-teambase-chip"

interface TrayProps {
	tray: TrayStore
	files: readonly StagedFile[]
}

export function AttachmentTray({ tray, files }: TrayProps) {
	const announce = useAnnounce()
	const [active, setActive] = useState(0)
	const [editing, setEditing] = useState<StagedFile | null>(null)
	const list = useRef<HTMLUListElement>(null)

	const rejected = tray.rejected()
	// The roving stop follows a shrinking list: without the clamp, removing the last chip would
	// leave the stop past the end and the whole strip out of the tab order.
	const stop = Math.min(active, Math.max(0, files.length - 1))

	/**
	 * The roving stop and the real focus are kept together: `stop` says which chip is the strip's one
	 * tab stop, and this puts the caret there whenever it moves — but only while the strip already
	 * had focus, so drawing the tray never takes focus away from the text box. The chip is found by
	 * its position rather than through a ref per chip, because every move rebuilds the list.
	 */
	useEffect(() => {
		const strip = list.current
		if (strip === null || !strip.contains(document.activeElement)) return
		strip.querySelectorAll<HTMLButtonElement>(".tb-tray__button")[stop]?.focus()
	}, [stop, files])

	const move = useCallback(
		(file: StagedFile, delta: number) => {
			const to = tray.move(file.uuid, delta)
			if (to === undefined) return
			announce("composer.attach.moved", {
				name: file.name,
				position: to + 1,
				total: files.length,
			})
			setActive(to)
		},
		[tray, announce, files.length],
	)

	const remove = useCallback(
		(file: StagedFile, index: number) => {
			tray.remove(file.uuid)
			announce("composer.attach.removed", { name: file.name })
			setActive(Math.max(0, Math.min(index, files.length - 2)))
		},
		[tray, announce, files.length],
	)

	/**
	 * The drop half of a pointer reorder. Which chip is being dragged travels in the drag's own data
	 * rather than in a variable beside it, so a drag that never ends — the pointer leaves the window,
	 * the browser cancels it — leaves nothing behind to clean up. A drag of files carries no index
	 * and is left to the window guard, which stages them.
	 */
	const onChipDrop = (event: React.DragEvent, index: number) => {
		const from = Number(event.dataTransfer.getData(CHIP_INDEX))
		if (
			!Number.isInteger(from) ||
			event.dataTransfer.getData(CHIP_INDEX) === "" ||
			from === index
		) {
			return
		}
		event.preventDefault()
		const moved = files[from]
		const to = tray.moveTo(from, index)
		if (to === undefined || moved === undefined) return
		announce("composer.attach.moved", { name: moved.name, position: to + 1, total: files.length })
		setActive(to)
	}

	const onChipKey = (event: React.KeyboardEvent, file: StagedFile, index: number) => {
		// Alt is what separates "move this chip" from "go to the next chip"; without the modifier
		// the arrows walk the strip, which is what a list of controls is expected to do.
		if (event.altKey && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
			event.preventDefault()
			move(file, event.key === "ArrowLeft" ? -1 : 1)
			return
		}
		if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
			const to = index + (event.key === "ArrowLeft" ? -1 : 1)
			if (to < 0 || to >= files.length) return
			event.preventDefault()
			setActive(to)
			return
		}
		if (event.key === "Home" || event.key === "End") {
			event.preventDefault()
			setActive(event.key === "Home" ? 0 : files.length - 1)
			return
		}
		if (event.key === "Delete" || event.key === "Backspace") {
			event.preventDefault()
			remove(file, index)
		}
	}

	const menuOf = (file: StagedFile, index: number): MenuItem[] => {
		const items: MenuItem[] = [
			{
				id: "caption",
				label: t("composer.attach.caption.open"),
				onSelect: () => setEditing(file),
			},
			{
				id: "left",
				label: t("composer.attach.moveLeft"),
				disabled: index === 0,
				onSelect: () => move(file, -1),
			},
			{
				id: "right",
				label: t("composer.attach.moveRight"),
				disabled: index === files.length - 1,
				onSelect: () => move(file, 1),
			},
		]
		if (file.state === "failed") {
			items.push({
				id: "retry",
				label: t("composer.attach.retry"),
				onSelect: () => tray.retry(file.uuid),
			})
		}
		items.push({
			id: "remove",
			label: t("composer.attach.remove"),
			danger: true,
			onSelect: () => remove(file, index),
		})
		return items
	}

	if (files.length === 0 && rejected.length === 0) return null

	return (
		<div className="tb-tray">
			{files.length === 0 ? null : (
				<ul ref={list} className="tb-tray__list" aria-label={t("composer.attach.tray")}>
					{files.map((file, index) => (
						<li key={file.uuid} className="tb-tray__item">
							{/* The chip is the interface's own pressable fact (ui.css §6.9), with the upload's
							    progress as a hairline along its foot; what stands under it — a caption, a refusal —
							    belongs to the file and not inside the pill. */}
							<div
								className={cx(
									"tb-chip",
									"tb-tray__chip",
									file.state === "failed" && "tb-tray__chip--failed",
								)}
							>
								<Menu
									trigger={
										<Button
											className="tb-tray__button"
											tabIndex={index === stop ? 0 : -1}
											aria-label={chipLabel(file, index, files.length)}
											onFocus={() => setActive(index)}
											onKeyDown={(event) => onChipKey(event, file, index)}
											// Dragging is the third way to reorder and the only one that is
											// pointer-only, which is why the menu and Alt+arrows exist beside it
											// (SC 2.5.7). It hangs on the chip's own control, because a list item is
											// not something a person can operate.
											draggable
											onDragStart={(event) => {
												event.dataTransfer.setData(CHIP_INDEX, String(index))
												event.dataTransfer.effectAllowed = "move"
											}}
											onDragOver={(event) => event.preventDefault()}
											onDrop={(event) => onChipDrop(event, index)}
											before={<Preview file={file} />}
											label={file.name}
										/>
									}
									items={menuOf(file, index)}
								/>
								<span className="tb-tray__size">{formatBytes(file.bytes)}</span>
								<Button
									quiet
									icon
									size="sm"
									className="tb-tray__remove"
									tabIndex={-1}
									aria-label={t("composer.attach.removeNamed", { name: file.name })}
									before={<Icon name="close" size={16} />}
									onClick={() => remove(file, index)}
								/>
								{file.state === "uploading" || file.state === "queued" ? (
									<div
										className="tb-tray__progress"
										role="progressbar"
										aria-label={t("composer.attach.progress", { name: file.name })}
										aria-valuemin={0}
										aria-valuemax={100}
										aria-valuenow={Math.round(file.progress * 100)}
									>
										<span
											className="tb-tray__progressBar"
											style={{ inlineSize: `${Math.round(file.progress * 100)}%` }}
										/>
									</div>
								) : null}
							</div>
							{/* Where the file came from (a long paste), under the chip like a caption so the
							    name keeps the chip's width; the chip's spoken name carries it too. */}
							{file.note === undefined ? null : (
								<span className="tb-tray__note" aria-hidden="true">
									{file.note}
								</span>
							)}
							{file.caption === "" ? null : (
								<span className="tb-tray__caption">{file.caption}</span>
							)}
							{file.error === undefined ? null : (
								<p className="tb-tray__error" role="alert">
									{translate(file.error.key, file.error.args)}
								</p>
							)}
						</li>
					))}
				</ul>
			)}

			{rejected.map((refusal) => (
				<p key={refusal.name} className="tb-tray__error" role="alert">
					{translate(refusal.key, refusal.args)}
				</p>
			))}

			{editing === null ? null : (
				<CaptionDialog
					key={editing.uuid}
					file={editing}
					onClose={() => setEditing(null)}
					onSave={(uuid, caption, name) => {
						tray.setCaption(uuid, caption)
						tray.rename(uuid, name)
						setEditing(null)
					}}
				/>
			)}
		</div>
	)
}

/** The chip's picture: the thumbnail once the server has one, else the glyph of what it is. */
function Preview({ file }: { file: StagedFile }) {
	if (file.kind === "image" && file.thumbUrl !== undefined) {
		// Decorative: the chip's own accessible name already says which file this is.
		return <img className="tb-tray__thumb" src={file.thumbUrl} alt="" />
	}
	return <Icon name={file.kind === "image" ? "image" : "file"} size={16} />
}

interface CaptionProps {
	file: StagedFile
	onClose: () => void
	onSave: (uuid: string, caption: string, name: string) => void
}

/**
 * The caption *is* the alt text (spec 07 §4): one field, because two would mean asking twice for
 * the same sentence and getting it once. The label says what it is for, in the words that make
 * somebody write a useful one.
 */
function CaptionDialog({ file, onClose, onSave }: CaptionProps) {
	// Mounted per file (the caller keys it on the uuid), so the two fields start from that file and
	// no effect has to copy props into state.
	const [caption, setCaption] = useState(file.caption)
	const [name, setName] = useState(file.name)

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose()
			}}
			title={t("composer.attach.caption.title", { name: file.name })}
			footer={
				<div className="tb-dialog__actions">
					<Button label={t("composer.cancel")} onClick={onClose} />
					<Button
						variant="primary"
						label={t("composer.attach.caption.save")}
						onClick={() => onSave(file.uuid, caption.trim(), name)}
					/>
				</div>
			}
		>
			<Field label={t("composer.attach.rename.label")}>
				{(props) => (
					<input
						{...props}
						className="tb-field__control"
						value={name}
						onChange={(event) => setName(event.target.value)}
					/>
				)}
			</Field>
			<Field label={t("composer.attach.caption.label")} hint={t("composer.attach.caption.hint")}>
				{(props) => (
					<textarea
						{...props}
						className="tb-field__control"
						rows={3}
						value={caption}
						onChange={(event) => setCaption(event.target.value)}
					/>
				)}
			</Field>
		</Dialog>
	)
}
